import classNames from 'classnames';
import React from 'react';
import { LocalizedHtml, useI18n } from '@udemy/i18n';
import { PlanPrice } from '../plan-price/plan-price.react-component';
import styles from "./plan-period.module.css";
export let PlanPeriodVariant = /*#__PURE__*/function (PlanPeriodVariant) {
  PlanPeriodVariant[PlanPeriodVariant["STARTING_AT_AFTER_TRIAL_PERIOD"] = 0] = "STARTING_AT_AFTER_TRIAL_PERIOD";
  PlanPeriodVariant[PlanPeriodVariant["STARTING_AT_AFTER_TRIAL_PERIOD_DAYS"] = 1] = "STARTING_AT_AFTER_TRIAL_PERIOD_DAYS";
  PlanPeriodVariant[PlanPeriodVariant["STARTING_AT_PER_MONTH"] = 2] = "STARTING_AT_PER_MONTH";
  PlanPeriodVariant[PlanPeriodVariant["STARTS_AT_AFTER_TRIAL_PERIOD"] = 3] = "STARTS_AT_AFTER_TRIAL_PERIOD";
  PlanPeriodVariant[PlanPeriodVariant["STARTS_AT_AFTER_TRIAL_PERIOD_DAYS"] = 4] = "STARTS_AT_AFTER_TRIAL_PERIOD_DAYS";
  PlanPeriodVariant[PlanPeriodVariant["STARTS_AT_PER_MONTH"] = 5] = "STARTS_AT_PER_MONTH";
  PlanPeriodVariant[PlanPeriodVariant["PRICE_WITH_TRIAL"] = 6] = "PRICE_WITH_TRIAL";
  PlanPeriodVariant[PlanPeriodVariant["PRICE_WITH_TRIAL_PERIOD_DAYS"] = 7] = "PRICE_WITH_TRIAL_PERIOD_DAYS";
  PlanPeriodVariant[PlanPeriodVariant["PRICE_PER_MONTH"] = 8] = "PRICE_PER_MONTH";
  PlanPeriodVariant[PlanPeriodVariant["COMPARISON_PRICE"] = 9] = "COMPARISON_PRICE";
  PlanPeriodVariant[PlanPeriodVariant["COMPARISON_PRICE_WITH_DISCOUNT"] = 10] = "COMPARISON_PRICE_WITH_DISCOUNT";
  return PlanPeriodVariant;
}({});

// Provides override configurations:
// If single localized line is >42 characters
// AND one of the MULTILINE configurations is passed in,
// this will determine the amount of space between the lines
export let PlanPeriodStyle = /*#__PURE__*/function (PlanPeriodStyle) {
  PlanPeriodStyle[PlanPeriodStyle["SINGLE"] = 0] = "SINGLE";
  PlanPeriodStyle[PlanPeriodStyle["MULTILINE_MARGIN"] = 1] = "MULTILINE_MARGIN";
  PlanPeriodStyle[PlanPeriodStyle["MULTILINE_NO_MARGIN"] = 2] = "MULTILINE_NO_MARGIN";
  PlanPeriodStyle[PlanPeriodStyle["PLAN_COMPARISON"] = 3] = "PLAN_COMPARISON";
  return PlanPeriodStyle;
}({});
const CancelAnytime = ({
  style
}) => {
  const {
    gettext
  } = useI18n();
  if (style === PlanPeriodStyle.MULTILINE_NO_MARGIN || style === PlanPeriodStyle.MULTILINE_MARGIN) return /*#__PURE__*/React.createElement(React.Fragment, null, gettext('Cancel anytime'));
  return /*#__PURE__*/React.createElement(React.Fragment, null);
};
export const PlanPeriod = ({
  trialPeriodDays,
  listPriceText,
  showTrialDays,
  showStartingAtPrice,
  abbreviateStartingAtPrice,
  emphasizeListPriceText,
  discountPriceText,
  style = PlanPeriodStyle.SINGLE,
  textOverride,
  discountPriceTextClassName,
  listPriceTextClassName
}) => {
  const SINGLE_LINE_CHARACTER_LIMIT = 42;
  const MULTILINE_WRAP_LIMIT = 64;
  const {
    gettext,
    interpolate
  } = useI18n();

  // Returns copy w/o punctuation at the end if 'Cancel anytime.' is shown on a single line
  function getPlanPeriodText(planPeriodVariant, showEmphasizedPriceText) {
    // If text provided directly (as in case of a notice), simply use that text
    if (textOverride) return textOverride;
    const priceElement = /*#__PURE__*/React.createElement(PlanPrice, {
      listPriceText: listPriceText,
      discountPriceText: discountPriceText,
      discountPriceTextClassName: classNames(discountPriceTextClassName, {
        'ud-text-bold': showEmphasizedPriceText && discountPriceText
      }),
      listPriceTextClassName: classNames(listPriceTextClassName, {
        'ud-text-bold': showEmphasizedPriceText && !discountPriceText
      })
    });
    switch (planPeriodVariant) {
      case PlanPeriodVariant.STARTING_AT_AFTER_TRIAL_PERIOD:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('Starting at <span class="price">%(price)s</span> per month after trial'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.STARTING_AT_AFTER_TRIAL_PERIOD_DAYS:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: interpolate(gettext('Starting at <span class="price">%(price)s</span> per month after %(trialPeriodDays)s-day trial'), {
              trialPeriodDays
            }, true),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.STARTING_AT_PER_MONTH:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('Starting at <span class="price">%(price)s</span> per month'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.STARTS_AT_AFTER_TRIAL_PERIOD:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('Starts at <span class="price">%(price)s</span>/mo after trial'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.STARTS_AT_AFTER_TRIAL_PERIOD_DAYS:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: interpolate(gettext('Starts at <span class="price">%(price)s</span>/mo after %(trialPeriodDays)s-day trial'), {
              trialPeriodDays
            }, true),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.STARTS_AT_PER_MONTH:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('Starts at <span class="price">%(price)s</span>/mo'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.PRICE_WITH_TRIAL:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('<span class="price">%(price)s</span> per month after trial'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.PRICE_WITH_TRIAL_PERIOD_DAYS:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: interpolate(gettext('<span class="price">%(price)s</span> per month after %(trialPeriodDays)s-day trial'), {
              trialPeriodDays
            }, true),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.PRICE_PER_MONTH:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('<span class="price">%(price)s</span> per month'),
            interpolate: {
              price: priceElement
            }
          });
        }
      default:
        return '';
    }
  }

  // When 'Cancel anytime.' copy is displayed on one line, use this function to ensure proper localized punctuation added
  function getPlanPeriodSentence(planPeriodVariant, showEmphasizedPriceText) {
    // If text provided directly (as in case of a notice), simply use that text
    if (textOverride) return textOverride;
    const priceElement = /*#__PURE__*/React.createElement(PlanPrice, {
      listPriceText: listPriceText,
      discountPriceText: discountPriceText,
      discountPriceTextClassName: classNames(discountPriceTextClassName, {
        'ud-text-bold': showEmphasizedPriceText && discountPriceText
      }),
      listPriceTextClassName: classNames(listPriceTextClassName, {
        'ud-text-bold': showEmphasizedPriceText && !discountPriceText
      })
    });
    switch (planPeriodVariant) {
      case PlanPeriodVariant.STARTING_AT_AFTER_TRIAL_PERIOD:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('Starting at <span class="price">%(price)s</span> per month after trial. Cancel anytime.'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.STARTING_AT_AFTER_TRIAL_PERIOD_DAYS:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: interpolate(gettext('Starting at <span class="price">%(price)s</span> per month after %(trialPeriodDays)s-day trial. Cancel anytime.'), {
              trialPeriodDays
            }, true),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.STARTING_AT_PER_MONTH:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('Starting at <span class="price">%(price)s</span> per month. Cancel anytime.'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.STARTS_AT_AFTER_TRIAL_PERIOD:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('Starts at <span class="price">%(price)s</span>/mo after trial. Cancel anytime.'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.STARTS_AT_AFTER_TRIAL_PERIOD_DAYS:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: interpolate(gettext('Starts at <span class="price">%(price)s</span>/mo after %(trialPeriodDays)s-day trial. Cancel anytime.'), {
              trialPeriodDays
            }, true),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.STARTS_AT_PER_MONTH:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('Starts at <span class="price">%(price)s</span>/mo. Cancel anytime.'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.PRICE_WITH_TRIAL:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('<span class="price">%(price)s</span> per month after trial. Cancel anytime.'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.PRICE_WITH_TRIAL_PERIOD_DAYS:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: interpolate(gettext('<span class="price">%(price)s</span> per month after %(trialPeriodDays)s-day trial. Cancel anytime.'), {
              trialPeriodDays
            }, true),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.PRICE_PER_MONTH:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('<span class="price">%(price)s</span> per month. Cancel anytime.'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.COMPARISON_PRICE_WITH_DISCOUNT:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('<p>Starting at <span class="price">%(price)s</span>/month</p>'),
            interpolate: {
              price: priceElement
            }
          });
        }
      case PlanPeriodVariant.COMPARISON_PRICE:
        {
          return /*#__PURE__*/React.createElement(LocalizedHtml, {
            html: gettext('<p>Starting at <span class="price">%(price)s</span> per month</p>'),
            interpolate: {
              price: priceElement
            }
          });
        }
      default:
        return '';
    }
  }
  let multiline = style === PlanPeriodStyle.MULTILINE_MARGIN || style === PlanPeriodStyle.MULTILINE_NO_MARGIN;
  let planPeriodVariant;
  let planPeriodText = '';
  // This logic is designed to treat a 0-day trial in the same way as no trial at all.
  const trialPeriodDaysUnwrapped = trialPeriodDays !== null && trialPeriodDays !== void 0 ? trialPeriodDays : 0;
  if (showStartingAtPrice && trialPeriodDaysUnwrapped > 0 && showTrialDays) {
    planPeriodVariant = abbreviateStartingAtPrice ? PlanPeriodVariant.STARTS_AT_AFTER_TRIAL_PERIOD_DAYS : PlanPeriodVariant.STARTING_AT_AFTER_TRIAL_PERIOD_DAYS;
  }
  if (showStartingAtPrice && trialPeriodDaysUnwrapped > 0 && !showTrialDays) {
    planPeriodVariant = abbreviateStartingAtPrice ? PlanPeriodVariant.STARTS_AT_AFTER_TRIAL_PERIOD : PlanPeriodVariant.STARTING_AT_AFTER_TRIAL_PERIOD;
  }
  if (showStartingAtPrice && trialPeriodDaysUnwrapped <= 0) {
    planPeriodVariant = abbreviateStartingAtPrice ? PlanPeriodVariant.STARTS_AT_PER_MONTH : PlanPeriodVariant.STARTING_AT_PER_MONTH;
  }
  if (!showStartingAtPrice && trialPeriodDaysUnwrapped > 0 && showTrialDays) {
    planPeriodVariant = PlanPeriodVariant.PRICE_WITH_TRIAL_PERIOD_DAYS;
  }
  if (!showStartingAtPrice && trialPeriodDaysUnwrapped > 0 && !showTrialDays) {
    planPeriodVariant = PlanPeriodVariant.PRICE_WITH_TRIAL;
  }
  if (!showStartingAtPrice && trialPeriodDaysUnwrapped <= 0) {
    planPeriodVariant = PlanPeriodVariant.PRICE_PER_MONTH;
  }
  if (style === PlanPeriodStyle.PLAN_COMPARISON && !discountPriceText) {
    planPeriodVariant = PlanPeriodVariant.COMPARISON_PRICE;
  }
  if (style === PlanPeriodStyle.PLAN_COMPARISON && discountPriceText) {
    planPeriodVariant = PlanPeriodVariant.COMPARISON_PRICE_WITH_DISCOUNT;
  }
  planPeriodText = getPlanPeriodSentence(planPeriodVariant, emphasizeListPriceText);

  // If localized sentence shorter than 42 characters and parent component specifies multiline configuration,
  // split the copy across lines without punctuation. Otherwise, we can end up with just a single word on the
  // wrapped line
  if (typeof planPeriodText === 'string') {
    multiline = multiline && planPeriodText.length > SINGLE_LINE_CHARACTER_LIMIT && planPeriodText.length < MULTILINE_WRAP_LIMIT;
  }
  if (multiline) {
    planPeriodText = getPlanPeriodText(planPeriodVariant, emphasizeListPriceText);
  }
  let planPeriodStyle = 'plan-period-one-line';
  if (style === PlanPeriodStyle.MULTILINE_MARGIN && multiline) {
    planPeriodStyle = 'plan-period-multiline';
  }
  if (style === PlanPeriodStyle.MULTILINE_NO_MARGIN && multiline) {
    planPeriodStyle = 'plan-period-multiline-no-margin';
  }
  const cancelAnytimeStyle = multiline ? 'cancel-anytime-multiline' : 'cancel-anytime-one-line';
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: styles[planPeriodStyle]
  }, planPeriodText), /*#__PURE__*/React.createElement("div", {
    className: styles[cancelAnytimeStyle]
  }, multiline && !textOverride && /*#__PURE__*/React.createElement(CancelAnytime, {
    style: style
  })));
};
//# sourceMappingURL=plan-period.react-component.js.map